import React, { useEffect, useState } from "react";
import { SubmitErrorHandler, SubmitHandler, useForm } from "react-hook-form";
import classNames from "classnames";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { AddressAutocompleteControlled } from "Components/Autocompletes/AddressAutocomplete";
import { ValidationSummary } from "Components/Inputs";
import * as Icons from "Components/Icons";
import { Modal, ModalBody, ModalHeader, ModalProps, Button, SpinningButton } from "Components/ReactstrapSlim";
import { SpaceType, SavedSearchSourceType, SavedSearchRequestClient, SavedSearchRequest, GeoSearchRequest, SavedSearchRequestMetadata, ReservationMethod } from "Services/API/API";
import { useApiClient } from "Services/API/useApiClient";
import SpaceTypeToggles from "../SpaceTypeToggles";

interface HomeBaseForm {
   location: string | undefined,
   spaceTypes: SpaceType,
}
const HomeBaseFormSchema: yup.SchemaOf<HomeBaseForm> = yup.object({
   location: yup.string().trim()
      .required("Location is required").defined(),
   spaceTypes: yup.number().
      optional().defined(),
}).defined();

interface HomeBaseModalProps extends ModalProps {
   searchId: Guid | undefined,
   toggle: () => void;
   onSuccess?: () => void;
}
export const HomeBaseModal: React.FC<HomeBaseModalProps> = ({ searchId, toggle, className, onSuccess, ...rest }) => {
   const savedSearchRequestClient = useApiClient(SavedSearchRequestClient);
   const [isLoaded, setIsLoaded] = useState(false);
   const [isSaving, setIsSaving] = useState(false);
   const [serverError, setServerError] = useState<Array<[string, string]>>([]);

   const formMethods = useForm<HomeBaseForm>({
      mode: "onTouched",
      defaultValues: { location: "", spaceTypes: SpaceType.Any },
      resolver: yupResolver(HomeBaseFormSchema),
   });

   const onValid: SubmitHandler<HomeBaseForm> = async data => {
      setIsSaving(true);
      try {
         const savedSearchRequest = new SavedSearchRequest({
            request: new GeoSearchRequest({
               address: data.location!,
               spaceTypes: data.spaceTypes,
               amenityIds: [],
               minPrice: undefined,
               maxPrice: undefined,
               minCapacity: undefined,
               reservationLengthMinutes: undefined,
               reservationMethod: ReservationMethod.Hourly,
               startTime: undefined
            }),
            metadata: new SavedSearchRequestMetadata({
               sourceType: SavedSearchSourceType.BrandedPortal,
               prdefinedText: data.location
            })
         });

         if (searchId && searchId != Guid.Empty) {
            await savedSearchRequestClient.update(searchId, savedSearchRequest);
         }
         else {
            await savedSearchRequestClient.create(savedSearchRequest);
         }

         onSuccess && onSuccess();
         toggle();
      } catch (err: any) {
         if (err.error) {
            setServerError([["location", err.error]]);
         }
         else if (err.errorMessage) {
            setServerError([["location", err.errorMessage || "Unexpected server error"]]);
         }
      } finally {
         setIsSaving(false);
      }
   }

   const onInvalid: SubmitErrorHandler<HomeBaseForm> = (errors) => {
      LiquidSpaceConfig.DEBUG && console.log(errors);
   }

   useEffect(() => {
      setIsLoaded(false);
      if (searchId) {
         savedSearchRequestClient.get(searchId).then(r => {
            if (r && r.request.constructor == GeoSearchRequest) {
               const geoSearchRequest = r.request as GeoSearchRequest;
               formMethods.reset({ location: geoSearchRequest.address, spaceTypes: geoSearchRequest.spaceTypes });
            }
         }).catch(err => setIsLoaded(true));
      }
      setIsLoaded(true);
   }, [searchId])

   return (
      <Modal className={classNames("homebase-modal", className)} size="lg" {...rest} toggle={toggle}>
         <ModalHeader toggle={toggle} lightWordsCount={1}>Edit Home base</ModalHeader>
         <ModalBody style={{ display: isLoaded ? "block" : "none" }} tag="form" onSubmit={formMethods.handleSubmit(onValid, onInvalid)}>
            <div className="homebase-modal-description p2 tc-secondary">Assigning your homebase allows you to see relevant locations and spaces.</div>
            <div className="homebase-modal-location">
               <AddressAutocompleteControlled control={formMethods.control} name="location" placeholder="Location" size="lg" tabIndex={undefined} icon={Icons.GPS} />
            </div>
            <div className="homebase-modal-worktype">
               <div className="homebase-modal-worktype-label lbl1 tc-secondary">What style of space do you need?</div>
               <SpaceTypeToggles control={formMethods.control} name="spaceTypes" />
               <ValidationSummary errors={formMethods.formState.errors} serverErrors={serverError} />
            </div>
            <div className="homebase-modal-buttons">
               <Button onClick={ev => { ev.preventDefault(); toggle(); }} size="lg" color="secondary" outline>Back</Button>
               <SpinningButton color="primary" isLoading={isSaving} size="lg">Save</SpinningButton>
            </div>
         </ModalBody>
      </Modal>
   );
}